import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * DiscountCode Schema
 * App-managed fixed-amount discount codes for registration payments
 * (ADR 0026). Registration-scoped: created by an admin on a registration,
 * entered by a registrant at the payment step. `amountOff` is CENTS and
 * subtracts per unit — per player on perPlayer products, once on perTeam —
 * after the early-bird/late/multi-team rules resolve (they stack).
 */

export const DiscountCodeSchema = z.object({
  _id: objectId,
  // Stored uppercase + trimmed; matched case-insensitively at apply time.
  code: z.string(),
  // Fixed amount off in CENTS (percent support is a possible later addition).
  amountOff: z.number(),
  active: z.boolean().default(true),
  // null/undefined = unlimited. A redemption = a completed discounted payment
  // (or a fully-discounted free completion), counted once per team.
  maxRedemptions: z.number().optional(),
  redemptionCount: z.number().default(0),
  expiresAt: z.coerce.date().optional(),
  registration: objectId,
  organization: objectId,
  ...timestampFields,
});

export type DiscountCode = z.infer<typeof DiscountCodeSchema>;

export const discountCodeMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  code: { label: 'Code', editable: true },
  amountOff: { label: 'Amount Off (cents)', editable: true },
  active: { label: 'Active', editable: true },
  maxRedemptions: { label: 'Max Redemptions', editable: true },
  redemptionCount: { label: 'Redemptions' },
  expiresAt: { label: 'Expires At', editable: true },
  registration: { ref: 'Registration', label: 'Registration' },
  organization: { ref: 'Organization', label: 'Organization' },
};
